import { escapeHtml } from '@/utils/htmlUtils'

import { whenText } from './claude-fleet.live'
import { isRunMine, runMatchesSearch } from './claude-fleet.runs'
import { describeRunPlace, isLaptopRun } from './claude-fleet.runners'
import type { FleetRun, FleetRunDetail, FleetRunQuestion, FleetRunSteps, FleetStep } from './claude-fleet.types'

/**
 * Watch live: following one fleet run from ADX while it works. Who may steer it, what each place a run can
 * be in can show, the run's question, and the links an action's toast and the fleet's DMs open.
 */

/** The fleet's limit for an answer to a run's question. */
export const FLEET_ANSWER_MAX_LENGTH = 2000

export interface FleetSteerState {
  steer: boolean
  /** Why the controls are off, for their tooltip. */
  why?: string
}

/**
 * Whether the viewer may say to, answer, pause, resume or stop the run: the fleet's own answer for this viewer,
 * or, from a fleet service older than starter steering, whether they are an operator.
 */
export function runSteer(
  run: Pick<FleetRunDetail, 'can'> | null | undefined,
  viewer: { isOperator: boolean; offReason?: string | null }
): FleetSteerState {
  if (run?.can) {
    return run.can.steer ? { steer: true } : { steer: false, why: run.can.why ?? 'Only the fleet operators and whoever started this run can steer it.' }
  }
  if (viewer.isOperator) return { steer: true }
  return { steer: false, why: viewer.offReason ?? 'Only fleet operators can steer this run.' }
}

/** Why a live runner's run shows its steps but no controls: it runs in an engineer's own Claude Code. */
export function runnerRunNote(run: Pick<FleetRun, 'ranOn' | 'runnerOwner' | 'runnerOwnerName'>): string | null {
  if (!isLaptopRun(run)) return null
  return (
    `This run is on ${describeRunPlace(run).label}. Its steps show below as the runner uploads them, about every 15 seconds. ` +
    "It runs in that engineer's own Claude Code, which the fleet cannot reach, so it cannot be messaged, paused or " +
    "asked a question from here. Its runner's owner or a fleet operator can stop it from the run's menu on the Runs tab."
  )
}

/** "Waiting for an answer until 12:30 (in 25m)", or that the run has stopped waiting. */
export function questionWaitText(question: Pick<FleetRunQuestion, 'waitUntil'>, now: number): string {
  const until = Date.parse(question.waitUntil)
  if (!Number.isFinite(until)) return 'Waiting for an answer.'
  if (until <= now) return 'The run stopped waiting and carries on with its own default.'
  const clock = new Date(until).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `Waiting for an answer until ${clock} (${whenText(question.waitUntil, now)}); after that the run carries on with its own default.`
}

/** A run queued with no id yet (a release ticket's test, a run started from the Loops tab), found by what it is for. */
export interface FleetRunFollow {
  activity: string
  /** The ticket or PR it is for, when it is for one. */
  key?: string
  /** When it was queued; the run it becomes starts after this. */
  since: string
}

const ACTIVITY = /^[a-z0-9-]{1,60}$/
const KEY = /^(?:[A-Z][A-Z0-9]+-\d+|[a-z0-9._-]+#\d+)$/
/** A run's start time on the record can be a little before the click's clock. */
const CLOCK_SLACK_MS = 60_000

/** The run to follow from a `?watch=<activity>&key=<key>&since=<iso>` link, or null for none or a malformed one. */
export function followFromQuery(query: Record<string, unknown>): FleetRunFollow | null {
  const { watch, key, since } = query
  if (typeof watch !== 'string' || !ACTIVITY.test(watch)) return null
  if (typeof since !== 'string' || !Number.isFinite(Date.parse(since))) return null
  if (key !== undefined && (typeof key !== 'string' || !KEY.test(key))) return null
  return { activity: watch, since, ...(typeof key === 'string' ? { key } : {}) }
}

/** The earliest start time that still counts as after the follow was queued, as an ISO time. */
export function followedSince(follow: FleetRunFollow): string {
  return new Date(Date.parse(follow.since) - CLOCK_SLACK_MS).toISOString()
}

/** How long a run started with no ticket or PR waits for the viewer's own run before it settles for another person's. */
const OWN_RUN_WAIT_MS = 180_000

/**
 * The run to follow among those that match what is followed and started after it was queued; null until one does.
 * The viewer's own run wins over another starter's, then the run that started closest to the queueing.
 * A run the viewer just started with no ticket or PR may still be queued, so for its first minutes only their own
 * run counts and Watch live never shows someone else's run of the same activity in its place.
 */
export function followedRun(
  runs: FleetRun[],
  follow: FleetRunFollow,
  viewerEmail?: string,
  { now = Date.now() }: { now?: number } = {}
): FleetRun | null {
  const from = Date.parse(followedSince(follow))
  const queuedAt = Date.parse(follow.since)
  const waitsForOwn = Boolean(viewerEmail) && !follow.key && now - queuedAt < OWN_RUN_WAIT_MS
  const matches = runs.filter(
    run =>
      run.activity === follow.activity &&
      Date.parse(run.startedAt) >= from &&
      (!follow.key || runMatchesSearch(run, follow.key)) &&
      (!waitsForOwn || isRunMine(run, viewerEmail!))
  )
  const isViewers = (run: FleetRun): number => (viewerEmail && isRunMine(run, viewerEmail) ? 0 : 1)
  const distance = (run: FleetRun): number => Math.abs(Date.parse(run.startedAt) - queuedAt)
  return matches.sort((a, b) => isViewers(a) - isViewers(b) || distance(a) - distance(b))[0] ?? null
}

const FLEET_PATH = '/admin/tools/claude-fleet'

export const watchRunPath = (runId: string): string => `${FLEET_PATH}?run=${encodeURIComponent(runId)}`

export function watchFollowPath(follow: FleetRunFollow): string {
  const query = new URLSearchParams({ watch: follow.activity, ...(follow.key ? { key: follow.key } : {}), since: follow.since })
  return `${FLEET_PATH}?${query.toString()}`
}

/** A toast's message with its Watch live link, as the toast's HTML description. */
export function withWatchLink(message: string, path: string): string {
  return `${escapeHtml(message)} <a href="${escapeHtml(path)}" data-testid="claude-fleet-watch-live-link">Watch live</a>`
}

/** A run queued just now with no id yet, to follow by what it is for. */
export function followNow(activity: string, key?: string): FleetRunFollow {
  return { activity, since: new Date().toISOString(), ...(key ? { key } : {}) }
}

/** The newest step the run has taken, across its subagents: what the live strip shows as "now". */
export function newestStep(steps: Pick<FleetRunSteps, 'groups'> | null | undefined): FleetStep | null {
  const all = (steps?.groups ?? []).flatMap(group => group.steps)
  const timed = all.filter(step => step.at)
  if (!timed.length) return all.at(-1) ?? null
  return timed.reduce((newest, step) => (step.at! > newest.at! ? step : newest))
}
